import React, { useEffect, useState } from 'react';
import { Stack, Typography, Button, Alert } from '@mui/material';
import AsyncAutoComplete from '../../Inputs/ResourceMapping/AsyncAutocomplete';
import { getAllocationProgramProjects, getBulkEditFinopsinputSecEnvOption, postBulkEditFinopsResourceMappingList } from 'service/service';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import RowTypography from 'components/Table/RowTypography';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import BulkUpdateConflictsModal from './BulkUpdateConflictModal';
import LoaderComponent from 'components/Loader';
import CommonSuccessModal from './CommonSuccessModal';
import RowChip from 'components/Table/RowChip';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { useTheme } from "@mui/material/styles";
import { getProgramProjectHelper } from './ResourceMappingHelper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface Option {
	value: string;
	label: string;
}

interface Row {
	id: string;
	[key: string]: any;
}

type ModalWidth = "sm" | "md" | "lg" | "xl";

interface CustomFormModalProps {
	queryPreference: any;
	bulkSelectedRows: any;
  	setBulkSelectedRows: any;
	onClose: () => void;
	onSave: (formData: FormData, changedCells: any, rowsToUpdate: any) => void;
	onCancel: () => void;
	onConfirm: () => void;
	onTitleChange: (newTitle: string) => void;
    onWidthChange: (newWidth: ModalWidth) => void;
	allocationType: {
		key: string;
		label: string;
	};
}

interface FormData {
	PepwiseProgram: string;
	PepwiseProject: string;
	PepwiseSector: string;
	PepwiseEnvironment: string;
}

const allocationCellFormatter = (
    groupBy: string,
    allocationType: any,
    idx: number,
	col: any,
    toggleSubRow: (name: string, values: any) => void,
	colors: any
) => {
    return ({ row, isCellSelected, column }: any) => {
        const isGroupBy = column.key.toLowerCase() === groupBy.toLowerCase();
		const hasChildren = !!row.values;
        const startDate = moment(row.startDate).format('YYYY-MM-DD');
        const endDate = (row.endDate === '9999-12-31' || row.endDate === '') 
			? moment().format('YYYY-MM-DD') 
			: moment(row.endDate).format('YYYY-MM-DD');

        const showExpander =
            column.key === allocationType.key &&
            idx === 0 &&
            isGroupBy &&
            hasChildren;

        const isChipColumn = ['sector', 'project', 'program'].includes(col.name);

		let cellValue: any = row[column.key];
		if (hasChildren) {
			cellValue = isGroupBy ? row.name : '';
		} else if (col.name === 'startDate') {
			cellValue = startDate;
		} else if (col.name === 'endDate') {
			cellValue = endDate;
		}

        return (
            <>
                {showExpander && (
                    <CellExpanderFormatter
                        isCellSelected={isCellSelected}
                        expanded={row.isExpanded === true}
                        onCellExpand={() => toggleSubRow(row.name, row.values)}
                    />
                )}
                {'   '}
                {isChipColumn ? (
                    row[column.key] && <RowChip value={row[column.key]} rowId={row.id} bgColor={colors.table.chipBackground} color={colors.table.chipText}/>
                ) : (
                    <RowTypography isSuccessor={!row.values}>
                        {cellValue}
                    </RowTypography>
                )}
            </>
        );
    };
};

const renderPepwiseChip = (row: any, columnKey: string, colors: any) => {
    const configMap: Record<string, { countKey: string; label: string }> = {
        PepwiseSector: { countKey: 'SectorCount', label: 'SECTORS' },
        PepwiseProject: { countKey: 'ProjectCount', label: 'PROJECTS' },
        PepwiseProgram: { countKey: 'ProgramCount', label: 'PROGRAMS' }
    };

    const config = configMap[columnKey];
    if (!config) return null;

    const count = row[config.countKey];
    const value = count > 1 ? `${count} ${config.label}` : row[columnKey];

    if (!value) return null;

    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <RowChip value={value} rowId={row.id} bgColor={colors.table.chipBackground} color={colors.table.chipText}/>
        </Stack>
    );
};

const pepwiseCellFormatter = (colors: any) => {
    return ({ row, column }: any) => {

        // ----- Allocation Percentage -----
        if (column.key === 'AllocPercent') {
            return (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
					<RowTypography isSuccessor={!row[column.key]}>
						{row[column.key] ? `${row[column.key]}%` : ''}
					</RowTypography>
				</Stack>
            );
        }

        // ----- Chip based columns -----
        if (['PepwiseSector', 'PepwiseProject', 'PepwiseProgram'].includes(column.key)) {
            return renderPepwiseChip(row, column.key, colors);
        }

        // ----- Default -----
        return (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
				<RowTypography isSuccessor={!row[column.key]}>{row[column.key] || ''}</RowTypography>
			</Stack>
        );
    };
};

const BulkEditModalConfirm: React.FC<CustomFormModalProps> = ({
	onClose,
	onSave,
	onCancel,
	onConfirm,
	bulkSelectedRows,
	setBulkSelectedRows,
	queryPreference,
	onTitleChange,
	onWidthChange,
	allocationType
}) => {
	const theme= useTheme();
	const colors = theme.palette;
	const isNestedTreeView = queryPreference.split('_')[0] === 'ResourceGroup';
	const defaultColumns = [ 'Sectors', 'Environment', 'Programs', 'Projects', 'Current Allocation %'];
	const defaultColumnNames = ['Resource', 'Sector', 'Environment', 'Project', 'Program', 'Allocation %', 'Start Date', 'End Date'];
	
	const initialFormData: FormData = {
		PepwiseProgram: '',
		PepwiseProject: '',
		PepwiseSector: '',
		PepwiseEnvironment: ''
		// DNAI_Value: ''
	};

	const [formData, setFormData] = useState<FormData>(initialFormData);
	const [groupId, setGroupId] = useState<string[]>([]);
	const [showbulkEditInput, setShowbulkEditInput] = useState<boolean>(true);
	const [showConflictModal, setShowConflictModal] = useState<boolean>(false);
	const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
	const [bulkEditResponse, setBulkEditResponse] = useState<any>(null);
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const [selectedProgramId, setSelectedProgramId] = useState<number>();
	const [selectedProjectId, setSelectedProjectId] = useState<number>();
	const [programOptions, setProgramOptions] = useState<Option[]>([]);
	const [projectOptions, setProjectOptions] = useState<Option[]>([]);
	const [sectorOptions, setSectorOptions] = useState<Option[]>([]);
	const [environmentOptions, setEnvironmentOptions] = useState<Option[]>([]);
	const [isProjectDisabled, setIsProjectDisabled] = useState(false);
	const [isProgramDisabled, setIsProgramDisabled] = useState(false);
	const [searchParams, ] = useSearchParams();
	const [parentRows, setParentRows] = useState<any[]>([]);

	const commonAsyncAutoCompleteSx = {
		width: '31rem',
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.input.border} !important`
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.DropdownV2.borderColorHover} !important`,
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.DropdownV2.borderColorHover} !important`,
		},
		'& .MuiAutocomplete-popupIndicator': {
			color: `${colors.DropdownV2.dropdownIconDefault} !important`,
			'& svg': {
				fill: `${colors.DropdownV2.dropdownIconDefault} !important`,
				color: `${colors.DropdownV2.dropdownIconDefault} !important`,
			},
		},
		'& .MuiAutocomplete-clearIndicator': {
			color: `${colors.DropdownV2.menuItemText} !important`,
			'& svg': {
				fill: `${colors.DropdownV2.menuItemText} !important`,
				color: `${colors.DropdownV2.menuItemText} !important`,
			},
		},
	};

	const commonAsyncAutoCompleteComponentsProps = {
		paper: {
			sx: {
				borderRadius: '4px',
				backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
				color: `${colors.DropdownV2.menuItemText} !important`,

				'&& .MuiAutocomplete-listbox': {
					paddingTop: '0 !important',
					paddingBottom: '0 !important',
				},

				'&& .MuiAutocomplete-option': {
					fontSize: '14px !important',
					fontFamily: 'Montserrat !important',
					color: `${colors.DropdownV2.menuItemText} !important`,
					backgroundColor: 'transparent !important',

					'& .MuiTypography-root': {
						color: `${colors.DropdownV2.menuItemText} !important`,
						fontSize: '14px !important',
						fontFamily: 'Montserrat !important',
						fontWeight: 'inherit !important',
					},
				},

				'&& .MuiAutocomplete-option:hover': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-focused': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-selected': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
					fontWeight: '500 !important',
				},

				'&& .MuiAutocomplete-option.Mui-selected .MuiTypography-root': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
					fontWeight: '500 !important',
				},

				'&& .MuiAutocomplete-option.Mui-selected:hover': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-selected.Mui-focused': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-selected.Mui-focused .MuiTypography-root': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
					fontWeight: '500 !important',
				},
			},
		},
	};

	const commonAsyncAutoCompleteTextFieldProps = {
		InputLabelProps: {
			sx: {
				'&&': { color: `${colors.DropdownV2.menuItemText} !important`, },
				'&.Mui-focused': { color: `${colors.DropdownV2.menuItemText} !important`, },
				'&.MuiInputLabel-shrink': { color: `${colors.DropdownV2.menuItemText} !important`, },
			},
		},
		InputProps: {
			sx: {
				'&&': { color: `${colors.input.text} !important` },
				'& input': { color: `${colors.input.text} !important` },
			},
		},
		sx: {
			'& .MuiOutlinedInput-root': {
				'& fieldset': { borderColor: `${colors.input.border} !important` },
				'&:hover fieldset': { borderColor: `${colors.DropdownV2.borderColorHover} !important`, },
				'&.Mui-focused fieldset': { borderColor: `${colors.DropdownV2.borderColorHover} !important`, },
			},
		},
	};
	
	const columnNames = [
		{ name: 'resource', label: 'Resource', isEditable: false },
		{ name: 'environment', label: 'Environment', isEditable: false },
		{ name: 'project', label: 'Project', isEditable: false },
		{ name: 'program', label: 'Program', isEditable: false },
		{ name: 'sector', label: 'Sector', isEditable: false },
		{ name: 'allocation', label: 'Allocation %', isEditable: false },
		{ name: 'startDate', label: 'Start Date', isEditable: false },
		{ name: 'endDate', label: 'End Date', isEditable: false },
	]

	const columns = [
		{ name: 'sNo', label: 'S.No' },
		{ name: 'Resource', label: 'Resource' },
		{ name: 'PepwiseSector', label: 'Sectors' },
		{ name: 'PepwiseEnvironment', label: 'Environment' },
		{ name: 'PepwiseProgram', label: 'Programs' },
		{ name: 'PepwiseProject', label: 'Projects' },
		{ name: 'AllocPercent', label: 'Current Allocation %' }
	];

	const isFormValid = () => {
		return (
		  selectedProgramId ||
		  selectedProjectId ||
		  formData.PepwiseProgram ||
		  formData.PepwiseProject ||
		  formData.PepwiseSector ||
		  formData.PepwiseEnvironment
		);
	  };

	const handleChange = (field: keyof FormData, value: string) => {
		setFormData((prevState) => {
			  // Otherwise, just update the specified field
			  return {
				...prevState,
				[field]: value,
			  };
		});
	};

	const handleCancel = () => {
		setFormData(initialFormData);
		onCancel();
	};

	// bulk update save api call
	const postBulkEdit = async (
		groupId: string[],
		formData: FormData,
		override: 'N' | 'Y' = 'N',
		skipconflict: 'N' | 'Y' = 'N',
		selectedProgramId?: number,
		selectedProjectId?: number
	) => {
		try {
			setIsLoading(true);
			const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			const BulkEditRes = (await postBulkEditFinopsResourceMappingList({
				groupId: groupId,
				...formData,
				override: override,
				skipconflict: skipconflict,
				programId: selectedProgramId,
				projectId: selectedProjectId,
				userTimezone: userTimezone
			})) as { response: any; data?: any; success?: boolean };


			// Check if `BulkEditRes` exists (success case)
			if (BulkEditRes.success) {
				if (BulkEditRes.success) {
					onTitleChange('Allocation Modified');
					onWidthChange('md');
					setBulkEditResponse(BulkEditRes.data);
					setShowbulkEditInput(false);
					setShowConflictModal(false);
					setShowSuccessModal(true);
					setFormData(initialFormData);
					setBulkSelectedRows({})
					return {success: true, data: BulkEditRes.data};
				} else {
					// Handle failure when success flag is false
					onTitleChange('Allocation Conflicts During Bulk Update');
					onWidthChange('md');
					setBulkEditResponse(BulkEditRes.data);
					setShowbulkEditInput(false);
					setShowConflictModal(true);
					setShowSuccessModal(false);
					return {success: false, data: BulkEditRes.data};
				}
			} else if (BulkEditRes.response.data) {
				const { success } = BulkEditRes.response.data;

				if (success) {
					onTitleChange('Allocation Modified');
					onWidthChange('md');
					setBulkEditResponse(BulkEditRes.response.data);
					setShowbulkEditInput(false);
					setShowSuccessModal(true);
					return {success: true, data: BulkEditRes.response.data};
				} else {
					// Handle failure when success flag is false
					onTitleChange('Allocation Conflicts During Bulk Update');
					onWidthChange('md');
					setBulkEditResponse(BulkEditRes.response.data);
					setShowbulkEditInput(false);
					setShowConflictModal(true);
					return {success: false, data: BulkEditRes.response.data};
				}
			}
		} catch (error) {
			console.log('Error in postBulkEdit:', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		const rows = Object.values(bulkSelectedRows);
		const groupIds = rows.map((row: any) => row.groupId)
		.filter(Boolean);

		const uniqueGroupIds = groupIds.reduce((acc: any[], currentGroupId: any) => {
			if (!acc.includes(currentGroupId)) {
				acc.push(currentGroupId);
			}
			return acc;
		}, []);

		setGroupId(uniqueGroupIds);
	}, [bulkSelectedRows]);

	const handleViewLogic = (bulkEditResponse: { result: any[] }, formData: FormData) => {
		let updatedRows;
		// Flat View Logic
		updatedRows = bulkEditResponse?.result?.reduce(
			(acc, r) => {
				const row = { ...r };
				(Object.keys(formData) as Array<keyof FormData>).forEach((key) => {

					if (formData[key]) {
						row[key] = formData[key];
						acc.changedCells = {
							...acc.changedCells,
							[r.groupId]: {
								...acc.changedCells[r.groupId],
								[key]: true
							}
						};
					}
				});
				acc[r.groupId] = row;
				return acc;
			},
			{ changedCells: {} } as typeof bulkSelectedRows
		);
		const { changedCells, ...rowsToUpdate } = updatedRows;
		
		return { changedCells, rowsToUpdate };
	// }
	}

	const handleSave = async (bulkSelectedRows: any, override: 'N' | 'Y' = 'N', skipconflict: 'N' | 'Y' = 'N') => {
		const response = await postBulkEdit(groupId, formData, override, skipconflict, selectedProgramId, selectedProjectId);
		if (response?.success) {
			let res = response.data;
			const { changedCells, rowsToUpdate } = handleViewLogic(res, formData);
			setBulkSelectedRows(rowsToUpdate);
			onSave(formData, changedCells, rowsToUpdate);
		}
	};

	// Fetch program and project options
	const getInputProgramProjectOptions = async (input: string, type: string) => {
		return getProgramProjectHelper(
			input,
			type
		);
	};

	const getProgramProjectOptions = async (input: string | undefined, type: 'project' | 'program'): Promise<Option[]> =>{
		const result = await getAllocationProgramProjects({ searchText: input?? "", searchType: type });
		if (type === 'program') result ?? setProgramOptions(result);
		else if(type === 'project') result ?? setProjectOptions(result);
		return result;
	}

	useEffect(() => {
		const fetchOptions = async () => {
			let programName: any = '';
			let projectName: any = '';
			if (searchParams.get('programName')) programName = searchParams.get('programName');
			if (searchParams.get('projectName')) projectName = searchParams.get('projectName');

			const [programOptions, projectOptions] =
				await Promise.all([
					getInputProgramProjectOptions(programName, 'program'),
					getInputProgramProjectOptions(projectName, 'project'),
				]);
			setProgramOptions(programOptions);
			setProjectOptions(projectOptions);

			if(searchParams.get('programId')) setSelectedProgramId(Number(searchParams.get('programId')));
			if (!!programName) {
				setFormData((prevState) => ({
					...prevState,
					PepwiseProgram: programName
				}));
				setIsProgramDisabled(true);
			}
			if(searchParams.get('projectId')) setSelectedProjectId(Number(searchParams.get('projectId')));
			if (!!projectName) {
				setFormData((prevState) => ({
					...prevState,
					PepwiseProject: projectName
				}));
				setIsProjectDisabled(true);
			}
		};
		fetchOptions();
		getInputSecEnvOptions();
	}, []);

	// fetch sector and environment options
	const getInputSecEnvOptions = async () => {
		try {
			const fieldsRes = await getBulkEditFinopsinputSecEnvOption();

				const sectorsOptions = fieldsRes.data.sectors.filter(Boolean).map((item: string) => ({
					value: item,
					label: item,
				}));

				const environmentsOptions = fieldsRes.data.environments.filter(Boolean).map((item: string) => ({
					value: item,
					label: item,
				}));
				setSectorOptions(sectorsOptions);
				setEnvironmentOptions(environmentsOptions);
		} catch (error) {
			console.error('Error fetching options:', error);
			return [];
		} 
	};

	function toggleSubRow(name: string, resourceTypes: any) {
		setSelectedRows((prev) => {
			const rows = [...prev];
			const rowIndex = rows.findIndex((r: any) => r.name === name);
			const row = rows[rowIndex];
			const newRows = [...rows];
			let children: Row[] = [];
			let childRows: Row[] = [];
			if (!row.isExpanded) {
				if (resourceTypes?.length > 0) {
					childRows = resourceTypes;
				}
				children = childRows.map((x: any) => {
					return { ...x, isChildren: true };
				});
				newRows.splice(Number(rowIndex) + 1, 0, ...children);
				newRows[rowIndex] = { ...row, isExpanded: true, children };
				localStorage.setItem('boxRows', JSON.stringify(newRows));
				row.isExpanded = true;
			} else {
				children = row.children ?? [];
				newRows.splice(Number(rowIndex) + 1, children.length);
				newRows[rowIndex] = { ...row, isExpanded: false };
				localStorage.setItem('boxRows', JSON.stringify(newRows));
				row.isExpanded = false;
			}
			return newRows;
		});
	}

	function getColumns(editedRows: Record<string, any>): Array<any> {
		const [groupBy] = queryPreference.split('_');

		return columnNames.map((col: any, idx: number) => ({
			key: col.name,
			name: col.label,
			formatter: allocationCellFormatter(
				groupBy,
				allocationType,
				idx,
				col,
				toggleSubRow,
				colors
			),
			width: 200
		}));
	}

	function getColumn(): Array<any> {
		return columns.map((col: any, idx: number) => {

			return {
				key: col.name,
				name: col.label,
				editable: false,
				formatter: pepwiseCellFormatter(colors),
				width: col.name === 'sNo' ? 70 : 168
			};
		});
	}


	useEffect(() => {
			if (isNestedTreeView) {
				const allRows = Object.values(bulkSelectedRows).filter((item: any) => item.isResource === true)
					.map((item: any, index: number) => ({
						...item,
						sNo: index + 1, // Assign serial number
					}));
				// .flatMap(
				// 	(item: any) => {
				// 		if (isNestedTreeView) {
				// 			return item.values; // Flatten nested rows if needed
				// 		}
				// 		return item; // Keep flat structure
				// 	}
				// );
	
				setParentRows(allRows.filter((item: any) => item));
			} else {
				const allRows = Object.values(bulkSelectedRows)
					.flatMap((item: any) => (isNestedTreeView ? item.values : item))
					.filter((item: any) => item?.values)
					.map((item: any, index: number) => ({
						...item,
						sNo: index + 1, // Assign serial number
					}));
				setParentRows(allRows.filter((item: any) => item?.values));
			}
			
		}, [bulkSelectedRows]);



	useEffect(() => {
		setSelectedRows(Object.values(bulkSelectedRows).filter((item: any) => !item.values));
	}, [bulkSelectedRows]);
	

	if (isLoading) {
		return <LoaderComponent bgColor={`${colors.modal.bg}`}/>;
	}

	if (showbulkEditInput) {
		return (
			<Stack spacing={2} sx={{ flexDirection: 'column', gap: 1, textAlign: 'left', p: '1rem', backgroundColor: `${colors.modal.bg} !important`, color: `${colors.modal.text} !important` }}>
				<Typography sx={{ fontSize: '14px' }}>{parentRows?.length} Resources selected</Typography>
				<Stack pb={3} marginBottom={1} borderBottom={`2px solid ${colors.dark.dark}`}>
					<CustomizedDataGrid
						title=""
						tableColumns={getColumn()}
						tableData={parentRows}
						defaultColumnNames={defaultColumns}
						autoSetDraggedColumns={false}
						isRenderPagination={false}
						isShowMoreButton={false}
						style={{ maxHeight: '150px' }}
					/>
				</Stack>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					justifyContent="center"
					alignItems="center"
					spacing={2}
					sx={{ width: '100%', pt: '0.7rem' }}
				>
					<AsyncAutoComplete
						textFieldLabel={'Program'}
						sx={commonAsyncAutoCompleteSx}
						componentsProps={commonAsyncAutoCompleteComponentsProps}
						customTextFieldProps={commonAsyncAutoCompleteTextFieldProps}
						popupIcon={<KeyboardArrowDownIcon />}
						inputValue={formData['PepwiseProgram']}
						value={formData['PepwiseProgram']}
						onChange={(event, value) => {
							const selectedProgram = programOptions.find((option) => option.label === value);
							const programId = selectedProgram && Number(selectedProgram.value);

							programId && setSelectedProgramId(programId);
							handleChange('PepwiseProgram', value);
						}}
						mappingType=""
						columnName={'PepwiseProgram'}
						options={programOptions}
						disabled={isProgramDisabled}
						fieldValueOptionsCallback={async (input) => await getProgramProjectOptions(input, 'program')}
					/>
					<AsyncAutoComplete
						textFieldLabel={'Project'}
						sx={commonAsyncAutoCompleteSx}
						componentsProps={commonAsyncAutoCompleteComponentsProps}
						customTextFieldProps={commonAsyncAutoCompleteTextFieldProps}
						popupIcon={<KeyboardArrowDownIcon />}
						inputValue={formData['PepwiseProject']}
						value={formData['PepwiseProject']}
						onChange={(event, value) => {
							const selectedProject = projectOptions.find((option) => option.label === value);
							const projectId = selectedProject && Number(selectedProject.value);

							projectId && setSelectedProjectId(projectId);
							handleChange('PepwiseProject', value);
						}}
						mappingType=""
						columnName={'PepwiseProject'}
						options={projectOptions}
						disabled={isProjectDisabled}
						fieldValueOptionsCallback={async (input) => await getProgramProjectOptions(input, 'project')}
					/>
				</Stack>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					justifyContent="center"
					alignItems="center"
					spacing={2}
					sx={{ width: '100%' }}
				>
					<AsyncAutoComplete
						textFieldLabel={'Sector'}
						sx={commonAsyncAutoCompleteSx}
						componentsProps={commonAsyncAutoCompleteComponentsProps}
						customTextFieldProps={commonAsyncAutoCompleteTextFieldProps}
						popupIcon={<KeyboardArrowDownIcon />}
						inputValue={formData['PepwiseSector']}
						value={formData['PepwiseSector']}
						onChange={(event, value) => handleChange('PepwiseSector', value)}
						mappingType="finopsResourceMapping"
						columnName={'PepwiseSector'}
						options={sectorOptions}
						// fieldValueOptionsCallback={async () => getInputSecEnvOptions('sectors')}
					/>
					<AsyncAutoComplete
						textFieldLabel={'Environment'}
						sx={commonAsyncAutoCompleteSx}
						componentsProps={commonAsyncAutoCompleteComponentsProps}
						customTextFieldProps={commonAsyncAutoCompleteTextFieldProps}
						popupIcon={<KeyboardArrowDownIcon />}
						inputValue={formData['PepwiseEnvironment']}
						value={formData['PepwiseEnvironment']}
						onChange={(event, value) => handleChange('PepwiseEnvironment', value)}
						mappingType="finopsResourceMapping"
						columnName={'PepwiseEnvironment'}
						options={environmentOptions}
						// fieldValueOptionsCallback={async () => getInputSecEnvOptions('environments')}
					/>
				</Stack>
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Alert
						severity="info"
						sx={{
							display: "flex",
    						alignItems: "center",
							 backgroundColor: `${colors.modal.bg} !important`,
							 color: `${colors.modal.text} !important`,
							'& .MuiAlert-icon': {
								fontSize: '1.2rem'
							}
						}}
					>
						<Typography sx={{ fontSize: '14px' }}>
							Resource will be 100% allocated to the selected project,
						</Typography>
						<Typography sx={{ fontSize: '14px' }}>during bulk update.</Typography>
					</Alert>
					<Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 2, p: '7px' }}>
						<Button variant="pepwiseOutline" sx={{ width: '8rem', color: `${colors.buttons.primary.color}` }} onClick={handleCancel}>
							CANCEL
						</Button>
						<Button
							disabled={!isFormValid()}
							variant="pepwiseSecondary"
							sx={{ width: '8rem' }}
							onClick={() => handleSave(bulkSelectedRows)}
						>
							UPDATE
						</Button>
					</Stack>
				</Stack>
			</Stack>
		);
	}

	if (showConflictModal) {
		return (
			<BulkUpdateConflictsModal
				onCancel={handleCancel}
				onTitleChange={onTitleChange}
				onWidthChange={onWidthChange}
				bulkEditResponse={bulkEditResponse}
				bulkSelectedRows={bulkSelectedRows}
				handleSave={handleSave}
				formData={formData}
				allocationType={allocationType}
				queryPreference={queryPreference}
			/>
		);
	}

	if (showSuccessModal) {
		return (
			<CommonSuccessModal
				onTitleChange={onTitleChange}
				onWidthChange={onWidthChange}
				onCancel={handleCancel}
				getColumns={() => getColumns(selectedRows)}
				messageTitle="Resource Allocation Successful"
				bulkEditResponse={bulkEditResponse}
				defaultColumnNames={defaultColumnNames}
				buttonLabel="CLOSE"
				onConfirm={onConfirm}
				successMessage={`resource${bulkEditResponse.result.length>1?'s':""} allocated successfully`}
				allocationType={allocationType}
			/>
		);
	}
	return null;
};

export default BulkEditModalConfirm;
